import { isEqual, isEqualWith, sum } from "lodash";

import { trimExcessSignificance } from "@citrine/utils/rounding";
import { stringComparator } from "@citrine/utils/sorting";
import { isDefinedEntry } from "@citrine/utils/types";

import type { ComboCandidate } from "../apex";
import type { AuditInfo } from "../common";
import { toRoundedNominalRealValue } from "../data-object";
import type { IDescriptor } from "../descriptor";
import { getDescriptorKey, isIntegerDescriptor } from "../descriptor";
import type { DesignWorkflowExecution } from "../design-workflow-execution";
import { isOfType } from "../utils";

// "C": Categories
//   "cp": mapping of categories to probabilities
export interface CategoricalCandidateValue {
  type: "C";
  cp: Partial<Record<string, number>>;
}
// "F": ChemicalFormula
//   "f": formula
export interface ChemicalFormulaCandidateValue {
  type: "F";
  f: string;
}
// "M": Mixture
//   "q": mapping of components to quantities, the values might not sum to 1
//   "labels": mapping of labels to the ingredients that were used in the
//             formulation
export interface MixtureCandidateValue {
  type: "M";
  q: Partial<Record<string, number>>;
  l: Partial<Record<string, string[]>>;
}
// "R": Real
//   "m": Mean
//   "s": Standard Deviation
export interface RealCandidateValue {
  type: "R";
  m: number;
  s: number;
}
// "S": MolecularStructure
//   "s": SMILES string
export interface MolecularStructureCandidateValue {
  type: "S";
  s: string;
}

export type CandidateValue =
  | CategoricalCandidateValue
  | ChemicalFormulaCandidateValue
  | MixtureCandidateValue
  | RealCandidateValue
  | MolecularStructureCandidateValue;

export const descriptorTypeToCandidateValueType: Record<
  IDescriptor["type"],
  CandidateValue["type"]
> = {
  Categorical: "C",
  Formulation: "M",
  Inorganic: "F",
  Integer: "R",
  Organic: "S",
  Real: "R",
};

export const isCategoricalCandidateValue = isOfType<
  CategoricalCandidateValue,
  CandidateValue
>("C");

export const isChemicalFormulaCandidateValue = isOfType<
  ChemicalFormulaCandidateValue,
  CandidateValue
>("F");

export const isMixtureCandidateValue = isOfType<
  MixtureCandidateValue,
  CandidateValue
>("M");

export const isRealCandidateValue = isOfType<
  RealCandidateValue,
  CandidateValue
>("R");

export const isMolecularStructureCandidateValue = isOfType<
  MolecularStructureCandidateValue,
  CandidateValue
>("S");

export interface SimpleMaterial {
  identifiers: MaterialIdentifier;
  vars: Partial<Record<string, CandidateValue>>;
}

export function getMaterialNameFromIdentifiers(
  identifiers: MaterialIdentifier,
): string {
  if (identifiers.external.length > 0) {
    return identifiers.external.join(" ");
  }
  return identifiers.id;
}

export type MaterialLookup = Partial<Record<string, SimpleMaterial>>;

export interface MaterialIdentifier {
  /** `id` should be unique across all identifier objects in the response */
  id: string;
  /** `external` ids may be duplicated in other identifier objects in the same response */
  external: string[];
  material_template?: string | null;
  material_template_name?: string | null;
  process_template?: string | null;
  process_template_name?: string | null;
}

export interface ExecutionCandidateComment {
  id: string;
  message: string;
  created: AuditInfo;
}

export interface ExecutionCandidate {
  type: "pioneer";
  id: string;
  comments: ExecutionCandidateComment[];
  rank: number;
  material_id: string;
  identifiers: string[];
  primary_score: number;
  material: SimpleMaterial;
  hidden?: boolean;
  name?: string;
  pinned?: AuditInfo;
  created_from_id?: string | null;
}

/**
 * Only the hidden/name/pinned Candidate fields are editable.
 * Exactly one action field must be provided per patch.
 */
export type PatchCandidate = {
  id: string;
  type: ComboCandidate["type"];
} & (
  | { hidden: boolean; name?: never; pinned?: never }
  | { name: string; hidden?: never; pinned?: never }
  | { pinned: boolean; hidden?: never; name?: never }
);

/**
 * Removes key-value store resources that have been tacked onto the
 * ExecutionCandidate interface - useful for exporting candidates JSON data
 */
export const removeKVAttributesFromCandidate = (
  candidate: ExecutionCandidate,
) =>
  Object.fromEntries(
    Object.entries(candidate).filter(
      ([key]) =>
        key !== "hidden" &&
        key !== "named" &&
        key !== "pinned" &&
        key !== "filterColor",
    ),
  ) as ExecutionCandidate;

/**
 * Returns an array of values that have the highest probability in a Categorical
 * candidate value.
 */
export function getHighestProbableCategoricalValues(
  candidateValue: CategoricalCandidateValue,
) {
  // I don't think this will ever happen where there are no values
  if (!Object.keys(candidateValue.cp).length) {
    return { probability: 0, values: [] };
  }
  const highestProbability = Math.max(
    ...(Object.values(candidateValue.cp) as number[]),
  );
  const values = Object.entries(candidateValue.cp)
    .filter(([, probability]) => probability === highestProbability)
    .map(([category]) => category)
    .sort(stringComparator);

  return { probability: highestProbability, values };
}

export function getValueFromCandidate(
  candidateValue?: CandidateValue,
): string | number | undefined {
  switch (candidateValue?.type) {
    case "C":
      const sortedCP = Object.entries(
        candidateValue.cp as Record<string, number>,
      ).sort((cp1, cp2) => cp2[1] - cp1[1]);
      // Only return the category with the highest probability
      if (sortedCP.length >= 1) {
        return sortedCP[0][0];
      } else {
        return;
      }
    case "F":
      return candidateValue.f;
    case "M":
      const sortedQ = Object.entries(
        candidateValue.q as Record<string, number>,
      ).sort(([, quantity1], [, quantity2]) => quantity2 - quantity1);

      return sortedQ
        .map(
          ([ingredient, quantity]) =>
            ingredient + ": " + toRoundedNominalRealValue(quantity)!,
        )
        .join("; ");
    case "R":
      return candidateValue.m;
    case "S":
      return candidateValue.s;
  }
}

/** Does not account for uncertainty, hence usage for inputs and features only */
export function getInputOrFeatureDisplayValue(
  material: SimpleMaterial,
  descriptor: IDescriptor,
) {
  const value = getValueFromCandidate(
    material.vars[getDescriptorKey(descriptor)],
  );

  return typeof value === "number"
    ? isIntegerDescriptor(descriptor)
      ? value.toFixed(0)
      : value.toString()
    : value;
}

/** Rounds a display value string for a real descriptor using standard rounding */
export function roundCandidateDisplayValue(
  value: string,
  descriptor: IDescriptor,
): string {
  if (isIntegerDescriptor(descriptor)) {
    return value;
  }
  const num = parseFloat(value);
  return isNaN(num) ? value : (toRoundedNominalRealValue(num) ?? value);
}

// TODO: Remove and/or localize values
export const SCORE_NAME_MAP = {
  MLI: "Likelihood of Improvement",
  MEI: "Expected Improvement",
  MEV: "Expected Value",
} as const;

/**
 * Creates an array of data objects that can be used to generate CSV rows, along
 * with an array of ordered headers
 */
export function candidatesToCSV(
  execution: DesignWorkflowExecution,
  candidates: ExecutionCandidate[],
) {
  const { name: scoreName } = execution.score;
  const scoreDisplayName = scoreName || SCORE_NAME_MAP[execution.score.type];

  // For building the headers array
  const keys = new Set<string>();
  const realKeys = new Set<string>();
  const categoricalKeys = new Set<string>();

  // zip score/candidates arrays into a single array
  const data = candidates.map((candidate) => ({
    [scoreDisplayName]: candidate.primary_score ?? "N/A",
    ...Object.entries(candidate.material.vars)
      .filter(isDefinedEntry)
      .reduce(
        (acc, [key, value]) => {
          acc[key] = isMixtureCandidateValue(value)
            ? {
                quantities: value.q,
                labels: value.l,
              }
            : getValueFromCandidate(value);
          keys.add(key);

          if (isRealCandidateValue(value)) {
            acc[generateUncertaintyKey(key)] = value.s;
            realKeys.add(key);
          } else if (isCategoricalCandidateValue(value)) {
            acc[generateProbabilityKey(key)] = value.cp;
            categoricalKeys.add(key);
          }

          return acc;
        },
        {} as Partial<Record<string, any>>,
      ),
  }));

  const headers = getCSVHeaders(
    scoreDisplayName,
    keys,
    realKeys,
    categoricalKeys,
  );

  return {
    data,
    headers,
  };
}

/** For real values */
function generateUncertaintyKey(key: string) {
  return `Uncertainty of ${key}`;
}

/** For categorical values */
function generateProbabilityKey(key: string) {
  return `Probabilities of ${key}`;
}

/**
 * Creates an array of headers for the candidates, sorted alphabetically with
 * uncertainty/probability columns situated next to real/categorical value
 * columns, and the score column prepended at the start of the array.
 */
function getCSVHeaders(
  scoreDisplayName: string,
  descriptorKeys: Set<string>,
  realKeys: Set<string>,
  categoricalKeys: Set<string>,
): string[] {
  const headers = [];

  Array.from(descriptorKeys)
    .sort(stringComparator)
    .forEach((key) => {
      headers.push(key);

      if (realKeys.has(key)) {
        headers.push(generateUncertaintyKey(key));
      } else if (categoricalKeys.has(key)) {
        headers.push(generateProbabilityKey(key));
      }
    });

  headers.unshift(scoreDisplayName);
  return headers;
}

export function getCategoricalCsvValue(value: CategoricalCandidateValue) {
  const sortedEntries = Object.entries(value.cp as Record<string, number>).sort(
    (cp1, cp2) => cp2[1] - cp1[1],
  );

  return sortedEntries
    .map(
      ([category, probability]) =>
        `${category}: ${trimExcessSignificance(probability)}`,
    )
    .join("  |  ");
}

export function getMixtureCsvValue(value: MixtureCandidateValue) {
  const sortedQuantities = Object.entries(
    value.q as Record<string, number>,
  ).sort(([, quantity1], [, quantity2]) => quantity2 - quantity1);

  return sortedQuantities
    .map(
      ([ingredient, quantity]) =>
        `${ingredient}: ${trimExcessSignificance(quantity)}`,
    )
    .join(", ");
}

export const getDefaultCandidateName = (rank: number) =>
  `Candidate ${`000${rank}`.slice(-4)}`;

export function getCandidateName(candidate: ComboCandidate) {
  return candidate.name ?? getDefaultCandidateName(candidate.rank);
}

export interface CandidateModificationTemplate {
  candidate_id: string;
  descriptors: IDescriptor[];
  material: SimpleMaterial;
  material_id: string;
  name: string;
}

export function getIngredientTotal(mixture: MixtureCandidateValue) {
  return sum(Object.values(mixture.q) as number[]);
}

function ingredientOrLabelQuantitiesSort(
  [key1, quantity1]: [string, number],
  [key2, quantity2]: [string, number],
) {
  return quantity1 !== quantity2
    ? quantity2 - quantity1
    : stringComparator(key1, key2);
}

/**
 * Converts a mapping of ingredients to values to a list of (ingredient, value)
 * pairs
 *
 * @param { MixtureCandidateValue | undefined } mixture
 * @returns [string, number | undefined][]
 */
export function getSortedIngredientPercentages(mixture: MixtureCandidateValue) {
  // avoid unlikely divide by 0 possibility by falling back to 1
  const totalIngredientQuantity = getIngredientTotal(mixture) || 1;

  return (Object.entries(mixture.q) as [string, number][])
    .sort(ingredientOrLabelQuantitiesSort)
    .map<
      [string, number]
    >(([ingredient, quantity]) => [ingredient, quantity / totalIngredientQuantity]);
}

/**
 * Creates a list of label quanitites for a mixture candidate.
 *
 * For all labels in the candidate, sum the combined percentages of label ingredients.
 *
 *
 * @param { MixtureCandidateValue } mixture
 * @param { [string, number | undefined][] } mixtureData – list of (ingredient, percentage) pairs
 * @returns [string, number][]
 */
export function getSortedLabelPercentages(
  mixture: MixtureCandidateValue,
): [string, number][] {
  // avoid unlikely divide by 0 possibility by falling back to 1
  const totalIngredientQuantity = getIngredientTotal(mixture) || 1;

  // Sum the amounts of each ingredient included in a label
  const labelQuantites = (
    Object.entries(mixture.l) as [string, string[]][]
  ).map(([label, ingredients]): [string, number] => {
    return [
      label,
      sum(ingredients.map((ingredient) => mixture.q[ingredient] ?? 0)) /
        totalIngredientQuantity,
    ];
  });

  return labelQuantites.sort(ingredientOrLabelQuantitiesSort);
}

export function getLabelsWithIngredients(mixture: MixtureCandidateValue) {
  const totalIngredientQuantity = getIngredientTotal(mixture) || 1;
  const sortedLabels = getSortedLabelPercentages(mixture);

  const getIngredientPercentage = (ingredient: string): number =>
    (mixture.q[ingredient] ?? 0) / totalIngredientQuantity;

  const labelsWithIngredients: {
    label: string | null;
    labelPercentage: number;
    ingredients: [string, number][];
  }[] = sortedLabels
    .map(([label, labelPercentage]) => {
      const ingredients = (mixture.l[label] ?? [])
        .map((ingredient): [string, number] | undefined =>
          mixture.q[ingredient]
            ? [ingredient, getIngredientPercentage(ingredient)]
            : undefined,
        )
        .filter(Boolean)
        .sort(ingredientOrLabelQuantitiesSort);

      return {
        label,
        labelPercentage,
        ingredients,
      };
    })
    // filter out labels with no ingredients
    .filter(({ labelPercentage }) => labelPercentage > 0);

  const labeledIngredients = new Set(Object.values(mixture.l).flat());

  const unlabelledIngredients = Object.entries(mixture.q).filter(
    ([ingredient]) =>
      !labeledIngredients.has(ingredient) && mixture.q[ingredient],
  );

  if (unlabelledIngredients.length > 0) {
    labelsWithIngredients.push({
      label: null,
      labelPercentage: sum(
        unlabelledIngredients.map(([, percentage]) => percentage ?? 0),
      ),
      ingredients: unlabelledIngredients
        .map(([ingredient, percentage]): [string, number] => [
          ingredient,
          percentage ?? 0,
        ])
        .sort(ingredientOrLabelQuantitiesSort),
    });
  }

  return labelsWithIngredients;
}

export function areMixtureCandidateValuesEqual(
  mixture1: MixtureCandidateValue | null,
  mixture2: MixtureCandidateValue | null,
) {
  return isEqualWith(mixture1, mixture2, function (value1, value2) {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return isEqual(
        // Sometimes the ingredient arrays in the label mappings are in different orders
        // between the flat and local formulations for whatever reason
        [...value1].sort(stringComparator),
        [...value2].sort(stringComparator),
      );
    }
  });
}
